import { symbol as symbol$1, equals } from '../../Equal/dist/effect-Equal.esm.js';
import { dual, pipe } from '../../Function/dist/effect-Function.esm.js';
import { symbol, combine, hash } from '../../Hash/dist/effect-Hash.esm.js';
import { toString, toJSON, NodeInspectSymbol } from '../../Inspectable/dist/effect-Inspectable.esm.js';
import { isSome } from '../../Option/dist/effect-Option.esm.js';
import { pipeArguments } from '../../Pipeable/dist/effect-Pipeable.esm.js';
import { isObject } from '../../Predicate/dist/effect-Predicate.esm.js';
import { empty as empty$1, fromIterable as fromIterable$1, findFirst, getOrder as getOrder$1, first, keys as keys$1, reduce as reduce$1, removeFirst, has as has$1, insert, size as size$1, values as values$1 } from '../../RedBlackTree/dist/effect-RedBlackTree.esm.js';

/**
 * @since 2.0.0
 */
const TypeId = /*#__PURE__*/Symbol.for("effect/SortedMap");

/**
 * @since 2.0.0
 * @category symbol
 */

/**
 * @since 2.0.0
 * @category models
 */

const SortedMapProto = {
  [TypeId]: TypeId,
  [symbol]() {
    return pipe(hash(this.tree), combine(hash("effect/SortedMap")));
  },
  [symbol$1](that) {
    return isSortedMap(that) && equals(this.tree, that.tree);
  },
  [Symbol.iterator]() {
    return this.tree[Symbol.iterator]();
  },
  toString() {
    return toString(this.toJSON());
  },
  toJSON() {
    return {
      _id: "SortedMap",
      values: Array.from(this).map(toJSON)
    };
  },
  [NodeInspectSymbol]() {
    return this.toJSON();
  },
  pipe() {
    return pipeArguments(this, arguments);
  }
};
const makeImpl = tree => {
  const self = Object.create(SortedMapProto);
  self.tree = tree;
  return self;
};

/**
 * @since 2.0.0
 * @category refinements
 */
const isSortedMap = u => isObject(u) && TypeId in u;

/**
 * @since 2.0.0
 * @category constructors
 */
const empty = ord => makeImpl(empty$1(ord));

/**
 * @since 2.0.0
 * @category constructors
 */
const fromIterable = ord => iterable => makeImpl(fromIterable$1(ord)(iterable));

/**
 * @since 2.0.0
 * @category constructors
 */
const make = ord => (...entries) => fromIterable(ord)(entries);

/**
 * @since 2.0.0
 * @category predicates
 */
const isEmpty = self => size(self) === 0;

/**
 * @since 2.0.0
 * @category predicates
 */
const isNonEmpty = self => size(self) > 0;

/**
 * @since 2.0.0
 * @category getters
 */
const entries = self => self[Symbol.iterator]();

/**
 * @since 2.0.0
 * @category elements
 */
const get = /*#__PURE__*/dual(2, (self, key) => findFirst(self.tree, key));

/**
 * Gets the `Order<K>` that the `SortedMap<K, V>` is using.
 *
 * @since 2.0.0
 * @category getters
 */
const getOrder = self => getOrder$1(self.tree);

/**
 * @since 2.0.0
 * @category elements
 */
const has = /*#__PURE__*/dual(2, (self, key) => isSome(get(self, key)));

/**
 * @since 2.0.0
 * @category elements
 */
const headOption = self => first(self.tree);

/**
 * @since 2.0.0
 * @category mapping
 */
const map = /*#__PURE__*/dual(2, (self, f) => reduce(self, empty(getOrder$1(self.tree)), (acc, v, k) => set(acc, k, f(v, k))));

/**
 * @since 2.0.0
 * @category getters
 */
const keys = self => keys$1(self.tree);

/**
 * @since 2.0.0
 * @category folding
 */
const reduce = /*#__PURE__*/dual(3, (self, zero, f) => reduce$1(self.tree, zero, f));

/**
 * @since 2.0.0
 * @category elements
 */
const remove = /*#__PURE__*/dual(2, (self, key) => makeImpl(removeFirst(self.tree, key)));

/**
 * @since 2.0.0
 * @category elements
 */
const set = /*#__PURE__*/dual(3, (self, key, value) => has$1(self.tree, key) ? makeImpl(insert(removeFirst(self.tree, key), key, value)) : makeImpl(insert(self.tree, key, value)));

/**
 * @since 2.0.0
 * @category getters
 */
const size = self => size$1(self.tree);

/**
 * @since 2.0.0
 * @category getters
 */
const values = self => values$1(self.tree);

export { empty, entries, fromIterable, get, getOrder, has, headOption, isEmpty, isNonEmpty, isSortedMap, keys, make, map, reduce, remove, set, size, values };
